import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';

interface TypeTruncatedText {
  text: string;
  Icon?: ReactNode | null;
  dataTestId?: string;
}

const TruncatedText: FC<TypeTruncatedText> = ({
  text,
  Icon = null,
  dataTestId,
}) => {
  const TOOLTIP_CLOSE_DELAY = 160;

  const textRef = useRef<HTMLSpanElement | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current != null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openTooltip = () => {
    clearCloseTimeout();

    if (isTruncated) {
      setIsTooltipOpen(true);
    }
  };

  const closeTooltip = () => {
    clearCloseTimeout();
    setIsTooltipOpen(false);
  };

  const scheduleTooltipClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsTooltipOpen(false);
      closeTimeoutRef.current = null;
    }, TOOLTIP_CLOSE_DELAY);
  };

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    let rafId: number | null = null;
    let timeoutId: number | null = null;

    const runMeasure = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const truncated =
          el.scrollWidth > Math.ceil(el.getBoundingClientRect().width);
        setIsTruncated((prev) => (prev !== truncated ? truncated : prev));
      });
    };

    const debouncedMeasure = () => {
      if (timeoutId != null) clearTimeout(timeoutId);
      timeoutId = window.setTimeout(runMeasure, 50);
    };

    debouncedMeasure();

    const resizeObserver = new ResizeObserver(() => {
      debouncedMeasure();
    });

    resizeObserver.observe(el);

    const onWinResize = () => debouncedMeasure();
    window.addEventListener('resize', onWinResize);

    return () => {
      try {
        resizeObserver.disconnect();
      } catch {}
      if (rafId != null) cancelAnimationFrame(rafId);
      if (timeoutId != null) clearTimeout(timeoutId);
      window.removeEventListener('resize', onWinResize);
    };
  }, [text]);

  useEffect(() => {
    const handleScroll = () => {
      closeTooltip();
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      clearCloseTimeout();
    };
  }, []);

  const content = (
    <span
      className="truncatedText"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        minWidth: 0,
        lineHeight: 1.8,
      }}
      onMouseEnter={openTooltip}
      onMouseLeave={scheduleTooltipClose}
    >
      {Icon && <span style={{ marginRight: 4, flex: '0 0 auto' }}>{Icon}</span>}
      <span
        data-test-id={dataTestId}
        key="text-span"
        ref={textRef}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'inline-block',
          flex: '1 1 0%',
          minWidth: 0,
        }}
      >
        {text}
      </span>
    </span>
  );

  return (
    <InfoTooltip
      title={isTruncated ? text : ''}
      enableNativeTitle={false}
      open={isTooltipOpen}
      onClose={closeTooltip}
      slotProps={{
        tooltip: {
          onMouseEnter: openTooltip,
          onMouseLeave: scheduleTooltipClose,
        },
      }}
    >
      {content}
    </InfoTooltip>
  );
};

export default TruncatedText;
